import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useSeo } from "@/lib/seo";

const ACTIVITES = [
  {
    n: "I",
    title: "Acquisition",
    body: "Identifier les opportunités.",
  },
  {
    n: "II",
    title: "Valorisation",
    body: "Révéler leur potentiel.",
  },
  {
    n: "III",
    title: "Investissement",
    body: "Construire durablement.",
  },
];

export default function Home() {
  useSeo({
    title: "Gambit Immobilier — Acquisition · Valorisation · Investissement",
    description:
      "Opérateur immobilier spécialisé dans l'acquisition, la valorisation et l'investissement. Près de vingt années d'expérience au service d'opérations solides et durables.",
    path: "/",
  });
  return (
    <div data-testid="page-home" className="bg-cream">
      {/* ---------------- Hero ------------------------------------------ */}
      <section className="relative">
        <h1 className="sr-only">Gambit Immobilier — Acquisition, valorisation et investissement immobilier</h1>
        <div className="mx-auto max-w-[1400px] px-6 pb-24 pt-20 md:px-12 md:pb-36 md:pt-28 lg:px-16 lg:pt-32">
          <div className="grid grid-cols-12 gap-y-12 md:gap-x-8">
            <div className="col-span-12">
              <Reveal>
                <img
                  src="/brand/logo-gambit-tight.svg"
                  alt="Gambit Immobilier"
                  data-testid="hero-logo"
                  className="h-auto w-auto max-w-full select-none md:h-[128px] lg:h-[148px]"
                  draggable={false}
                />
              </Reveal>

              <Reveal delay={0.14}>
                <p data-testid="hero-signature" className="kicker mt-10 md:mt-14">
                  Acquisition · Valorisation · Investissement
                </p>
              </Reveal>

              <Reveal delay={0.24}>
                <div className="mt-10 max-w-4xl space-y-5 font-serif text-[1.125rem] leading-[1.65] text-ink/80 md:mt-12 md:text-[1.25rem]">
                  <p data-testid="hero-paragraph-1">
                    Gambit est un opérateur immobilier spécialisé dans l'acquisition,
                    la valorisation et le développement d'opérations immobilières.
                  </p>
                  <p data-testid="hero-paragraph-2">
                    Près de vingt années d'expérience ont façonné une manière de
                    travailler fondée sur le terrain, l'analyse et la qualité des
                    décisions.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.36}>
                <div className="mt-12 flex flex-wrap items-center gap-4 md:gap-5">
                  <Link
                    to="/approche"
                    data-testid="hero-cta-approche"
                    className="group inline-flex items-center gap-3 bg-ink px-6 py-3.5 font-serif text-[1rem] text-cream transition-all duration-300 hover:bg-gold"
                  >
                    Notre approche
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <a
                    href="#contact"
                    data-testid="hero-cta-contact"
                    className="group inline-flex items-center gap-3 border border-ink/25 px-6 py-3.5 font-serif text-[1rem] text-ink transition-all duration-300 hover:border-gold hover:text-gold"
                  >
                    Nous contacter
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="hairline" />
      </div>

      {/* ---------------- Gambit (présentation) ------------------------- */}
      <section className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-32 lg:px-16">
        <div className="grid grid-cols-12 gap-y-10 md:gap-x-8">
          <Reveal className="col-span-12 md:col-span-4">
            <p className="kicker">Gambit</p>
            <h2 className="mt-6 font-serif text-[2rem] leading-[1.08] text-ink md:text-[2.5rem]">
              Une entreprise <span className="text-gold">construite par le terrain.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="col-span-12 md:col-span-7 md:col-start-6">
            <div className="prose-edito space-y-5 text-[1.125rem] leading-[1.7] md:text-[1.25rem]">
              <p>
                Créée en 2026, Gambit est née de près de vingt années
                d'expérience dans l'immobilier.
              </p>
              <p>
                Chaque opération est d'abord un lieu qui accueillera une vie,
                une famille, un projet. Cette responsabilité guide chacune de
                nos décisions.
              </p>
              <p>
                Le terrain apprend la patience. Les opérations forgent le
                discernement. L'expérience rappelle que les meilleures décisions
                sont celles qui traversent le temps.
              </p>
              <p>
                La confiance se construit avant la signature, dans la qualité
                des décisions prises.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- Nos activités (éditorial) --------------------- */}
      <section className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-28 lg:px-16">
        <ul>
          {ACTIVITES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.05} as="li">
              <article
                data-testid={`activite-${a.title.toLowerCase()}`}
                className="grid grid-cols-12 gap-x-6 border-t border-ink/12 py-10 last:border-b md:gap-x-10 md:py-14"
              >
                <div className="col-span-12 md:col-span-2">
                  <span className="font-serif text-[1.125rem] text-gold">
                    {a.n}
                  </span>
                </div>
                <h3 className="col-span-12 mt-2 font-serif text-[2rem] leading-[1.05] text-ink md:col-span-4 md:mt-0 md:text-[2.5rem]">
                  {a.title}
                </h3>
                <p className="col-span-12 mt-5 font-serif text-[1.125rem] leading-[1.6] text-ink/75 md:col-span-6 md:mt-1 md:text-[1.25rem]">
                  {a.body}
                </p>
              </article>
            </Reveal>
          ))}
        </ul>
      </section>

      {/* ---------------- Notre approche (lien) ------------------------- */}
      <section className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-32 lg:px-16">
        <div className="grid grid-cols-12 gap-y-10 md:gap-x-8">
          <Reveal className="col-span-12 md:col-span-4">
            <p className="kicker">Notre approche</p>
            <h2 className="mt-6 font-serif text-[2rem] leading-[1.08] text-ink md:text-[2.5rem]">
              Huit principes <br className="hidden md:block" /> simples.
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="col-span-12 md:col-span-7 md:col-start-6">
            <div className="prose-edito space-y-5 text-[1.125rem] leading-[1.7] md:text-[1.25rem]">
              <p>
                Notre manière de travailler s'est construite au fil des années.
              </p>
              <p>
                Elle repose aujourd'hui sur huit principes qui guident chacune de
                nos décisions.
              </p>
            </div>
            <div className="mt-10">
              <Link
                to="/approche"
                data-testid="cta-approche"
                className="group inline-flex items-center gap-3 border-b border-ink pb-2 font-serif text-[1.125rem] text-ink transition-all duration-300 hover:gap-5 hover:border-gold hover:text-gold"
              >
                Découvrir notre approche
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
